/**
 * Notification Service API
 * React Query hooks for Notification microservice
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query'
import { get, post, put, del, PaginatedResponse, PaginationParams } from '@/lib/api/client'
import { API_ENDPOINTS } from '@/lib/api/config'
import type {
  Notification,
  NotificationTemplate,
  NotificationPreferences,
  NotificationDigest,
  NotificationStats,
  BatchNotification,
  BatchResult,
  NotificationInbox,
} from '@/types/notification'

// Query keys
export const notificationKeys = {
  all: ['notifications'] as const,
  inbox: () => [...notificationKeys.all, 'inbox'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
  unread: () => [...notificationKeys.all, 'unread'] as const,
  detail: (id: string) => [...notificationKeys.all, 'detail', id] as const,
  templates: () => [...notificationKeys.all, 'templates'] as const,
  template: (id: string) => [...notificationKeys.templates(), id] as const,
  preferences: (userId: string) => [...notificationKeys.all, 'preferences', userId] as const,
  digests: () => [...notificationKeys.all, 'digests'] as const,
  stats: () => [...notificationKeys.all, 'stats'] as const,
}

// Inbox
export function useNotificationInbox(
  params?: { limit?: number; cursor?: string },
  options?: UseQueryOptions<NotificationInbox>
) {
  return useQuery({
    queryKey: [...notificationKeys.inbox(), params],
    queryFn: () => get<NotificationInbox>('/notifications/inbox', { params }),
    ...options,
  })
}

// Notifications List
export function useNotifications(
  params?: PaginationParams & { type?: string; category?: string; read?: boolean },
  options?: UseQueryOptions<PaginatedResponse<Notification>>
) {
  return useQuery({
    queryKey: [...notificationKeys.list(), params],
    queryFn: () => get<PaginatedResponse<Notification>>(API_ENDPOINTS.notification.list, { params }),
    ...options,
  })
}

// Unread count
export function useUnreadCount(options?: UseQueryOptions<number>) {
  return useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: () => get<{ count: number }>('/notifications/unread').then((r) => r.count),
    refetchInterval: 30000, // Poll every 30 seconds
    ...options,
  })
}

// Send notification
export function useSendNotification(options?: UseMutationOptions<Notification, Error, Partial<Notification>>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => post<Notification>(API_ENDPOINTS.notification.send, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() })
      queryClient.invalidateQueries({ queryKey: notificationKeys.stats() })
    },
    ...options,
  })
}

// Send batch notifications
export function useSendBatchNotifications(options?: UseMutationOptions<BatchResult, Error, BatchNotification>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => post<BatchResult>(API_ENDPOINTS.notification.batch, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() })
      queryClient.invalidateQueries({ queryKey: notificationKeys.stats() })
    },
    ...options,
  })
}

// Mark as read
export function useMarkAsRead(options?: UseMutationOptions<void, Error, string>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id) => post<void>(`/notifications/${id}/read`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.inbox() })
      queryClient.invalidateQueries({ queryKey: notificationKeys.unread() })
    },
    ...options,
  })
}

// Mark all as read
export function useMarkAllAsRead(options?: UseMutationOptions<void, Error, void>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => post<void>('/notifications/read-all', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.inbox() })
      queryClient.invalidateQueries({ queryKey: notificationKeys.unread() })
    },
    ...options,
  })
}

// Delete notification
export function useDeleteNotification(options?: UseMutationOptions<void, Error, string>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id) => del<void>(`/notifications/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.inbox() })
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() })
    },
    ...options,
  })
}

// Templates
export function useNotificationTemplates(
  params?: PaginationParams & { type?: string; category?: string },
  options?: UseQueryOptions<PaginatedResponse<NotificationTemplate>>
) {
  return useQuery({
    queryKey: [...notificationKeys.templates(), params],
    queryFn: () => get<PaginatedResponse<NotificationTemplate>>(API_ENDPOINTS.notification.templates, { params }),
    ...options,
  })
}

export function useNotificationTemplate(id: string, options?: UseQueryOptions<NotificationTemplate>) {
  return useQuery({
    queryKey: notificationKeys.template(id),
    queryFn: () => get<NotificationTemplate>(`${API_ENDPOINTS.notification.templates}/${id}`),
    enabled: !!id,
    ...options,
  })
}

export function useCreateNotificationTemplate(options?: UseMutationOptions<NotificationTemplate, Error, Partial<NotificationTemplate>>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => post<NotificationTemplate>(API_ENDPOINTS.notification.templates, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.templates() })
    },
    ...options,
  })
}

export function useUpdateNotificationTemplate(options?: UseMutationOptions<NotificationTemplate, Error, { id: string; data: Partial<NotificationTemplate> }>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }) => put<NotificationTemplate>(`${API_ENDPOINTS.notification.templates}/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.template(variables.id) })
      queryClient.invalidateQueries({ queryKey: notificationKeys.templates() })
    },
    ...options,
  })
}

// Preferences
export function useNotificationPreferences(userId: string, options?: UseQueryOptions<NotificationPreferences>) {
  return useQuery({
    queryKey: notificationKeys.preferences(userId),
    queryFn: () => get<NotificationPreferences>(API_ENDPOINTS.notification.preferences(userId)),
    enabled: !!userId,
    ...options,
  })
}

export function useUpdateNotificationPreferences(options?: UseMutationOptions<NotificationPreferences, Error, { userId: string; data: Partial<NotificationPreferences> }>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, data }) => put<NotificationPreferences>(API_ENDPOINTS.notification.preferences(userId), data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences(variables.userId) })
    },
    ...options,
  })
}

// Digests
export function useNotificationDigests(
  params?: PaginationParams & { type?: string },
  options?: UseQueryOptions<PaginatedResponse<NotificationDigest>>
) {
  return useQuery({
    queryKey: [...notificationKeys.digests(), params],
    queryFn: () => get<PaginatedResponse<NotificationDigest>>(API_ENDPOINTS.notification.digests, { params }),
    ...options,
  })
}

// Stats
export function useNotificationStats(
  params?: { start?: string; end?: string },
  options?: UseQueryOptions<NotificationStats>
) {
  return useQuery({
    queryKey: [...notificationKeys.stats(), params],
    queryFn: () => get<NotificationStats>(API_ENDPOINTS.notification.stats, { params }),
    ...options,
  })
}

// Track open
export function useTrackOpen() {
  return useMutation({
    mutationFn: (notificationId: string) =>
      post<void>(API_ENDPOINTS.notification.trackOpen, { notificationId }),
  })
}

// Track click
export function useTrackClick() {
  return useMutation({
    mutationFn: ({ notificationId, linkUrl }: { notificationId: string; linkUrl: string }) =>
      post<void>(API_ENDPOINTS.notification.trackClick, { notificationId, linkUrl }),
  })
}
